import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { Router } from "@angular/router";

import {
  HospitalUserAffiliationDTO,
  HospitalUserAffiliationList,
  HospitalUserAffiliationReadByUserIdRequestDTO,
} from "src/app/hospital/HospitalUserMapping/user-affiliation-mapping.interface";

import { HospitalUserAffiliationService } from "src/app/hospital/HospitalUserMapping/user-affiliation-mapping.service";
import { UserLoginService } from "src/app/login/userlogin.service";
import { BillerOperatorSelectionDTO } from "./end-user-bbps.interface";

export interface BillCategoryDTO {
  categoryId: number;
  categoryName: string;
  categoryDesc: string;
  isActive: number;
  isDeleted: number;
  createdBy?: string;
  createdOn?: string;
  modifiedBy?: string;
  modifiedOn?: string;
}

export interface BillPaymentCard {
  id: number;
  categoryId: string;
  name: string;
  category: string;
  icon: string;
  gradient: string;
  status: "active" | "maintenance";
}

@Component({
  selector: "app-end-user-bbps",
  templateUrl: "./end-user-bbps.component.html",
  styleUrls: ["./end-user-bbps.component.css"],
})
export class EndUserBbpsComponent implements OnInit {
  constructor(private router: Router) {}

  search = signal("");
  loading = signal(false);

  skeletons = Array(12).fill(0);

  userInfo: any;
  currentUserId = 0;

  selectedCategory: BillPaymentCard | null = null;
  isBillerOperatorPopupOpen = false;

  // More/Less toggle configuration.
  readonly defaultVisibleServiceCount = 12;
  showAllServices = signal(false);

  // Your dynamic API data will come here
  billCategories = signal<BillPaymentCard[]>([]);

  /**
   * Keep the complete filtered result here.
   * Do not slice this list, because the total count and More/Less visibility
   * need the complete number of matching services.
   */
  filtered = computed(() => {
    const q = this.search().toLowerCase().trim();

    return this.billCategories().filter((item) => {
      if (!q) return true;

      return (
        item.name.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
      );
    });
  });

  /**
   * Only the grid uses this computed list.
   * Initially it returns 12 records. More returns every matching record.
   */
  visibleServices = computed(() => {
    const services = this.filtered();

    return this.showAllServices()
      ? services
      : services.slice(0, this.defaultVisibleServiceCount);
  });

  /**
   * The toggle is displayed only when more than 12 matching services exist.
   */
  get canToggleServices(): boolean {
    return (
      this.filtered().length >
      this.defaultVisibleServiceCount
    );
  }

  ngOnInit(): void {
    this.loadCurrentUser();
    this.loadBillCategories();
  }

  private loadCurrentUser(): void {
    const storageKeys = [
      "userInfo",
      "UserInfo",
      "loggedInUser",
      "loginUser",
      "user",
    ];

    for (const key of storageKeys) {
      const storedValue = localStorage.getItem(key);

      if (!storedValue) {
        continue;
      }

      try {
        const parsedValue = JSON.parse(storedValue);

        this.userInfo =
          parsedValue?.data ??
          parsedValue?.item ??
          parsedValue?.user ??
          parsedValue;

        this.currentUserId = Number(
          this.userInfo?.userId ??
            this.userInfo?.UserId ??
            this.userInfo?.id ??
            this.userInfo?.Id ??
            0,
        );

        break;
      } catch {
        // Continue checking the next known local-storage key.
      }
    }
  }

  /**
   * Keeps the existing search flow and collapses the list whenever
   * the search text changes.
   */
  onSearchChanged(value: string): void {
    this.search.set(value || "");
    this.showAllServices.set(false);
  }

  /**
   * Simple More/Less toggle.
   */
  toggleServices(): void {
    this.showAllServices.set(
      !this.showAllServices(),
    );
  }

  loadBillCategories(): void {
    this.loading.set(true);

    // Replace this with your actual API call
    // this.bbpsService.Category_ReadAll().subscribe({
    //   next: (res) => {
    //     this.bindCategories(res.items || res);
    //     this.loading.set(false);
    //   },
    //   error: () => {
    //     this.loading.set(false);
    //   }
    // });

    // Temporary example if you already have data manually
    const apiData: BillCategoryDTO[] = [
      {
        categoryId: 1,
        categoryName: "Landline",
        categoryDesc: "Landline Bill Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 2,
        categoryName: "Broadband",
        categoryDesc: "Broadband Bill Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 3,
        categoryName: "Prepaid",
        categoryDesc: "Prepaid Recharge",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 4,
        categoryName: "Cable",
        categoryDesc: "Cable TV Bill Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 5,
        categoryName: "Postpaid",
        categoryDesc: "Postpaid Bill Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 6,
        categoryName: "Datacard Prepaid",
        categoryDesc: "Datacard Prepaid Recharge",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 7,
        categoryName: "Electricity",
        categoryDesc: "Electricity Bill Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 8,
        categoryName: "Gas",
        categoryDesc: "Gas Bill Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 9,
        categoryName: "Water",
        categoryDesc: "Water Bill Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 10,
        categoryName: "Insurance",
        categoryDesc: "Insurance Premium Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 11,
        categoryName: "EMI Payments",
        categoryDesc: "EMI / Loan Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 12,
        categoryName: "Traffic Challan",
        categoryDesc: "Traffic Challan Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 13,
        categoryName: "LPG Booking",
        categoryDesc: "LPG Cylinder Booking",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 14,
        categoryName: "Fastag",
        categoryDesc: "Fastag Recharge",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 15,
        categoryName: "Digital Voucher",
        categoryDesc: "Digital Voucher Purchase",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 16,
        categoryName: "Municipality Tax",
        categoryDesc: "Municipality Tax Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 17,
        categoryName: "DTH",
        categoryDesc: "DTH Recharge",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 18,
        categoryName: "Credit Card",
        categoryDesc: "Credit Card Bill Payment",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 19,
        categoryName: "string",
        categoryDesc: "string",
        isActive: 1,
        isDeleted: 0,
      },
      {
        categoryId: 20,
        categoryName: "string",
        categoryDesc: "string",
        isActive: 1,
        isDeleted: 0,
      },
    ];

    this.bindCategories(apiData);
    this.loading.set(false);
  }

  bindCategories(data: BillCategoryDTO[]): void {
    const mappedData = data
      .filter(
        (x) =>
          x.isActive === 1 &&
          x.isDeleted === 0 &&
          x.categoryName &&
          x.categoryName.toLowerCase() !== "string" &&
          x.categoryDesc &&
          x.categoryDesc.toLowerCase() !== "string",
      )
      .sort((a, b) => a.categoryId - b.categoryId)
      .map((x) => ({
        id: x.categoryId,
        categoryId: String(x.categoryId),
        name: x.categoryName,
        category: x.categoryDesc,
        icon: this.getCategoryIcon(x.categoryName),
        gradient: this.getCategoryGradient(
          x.categoryName,
        ),
        status: "active" as const,
      }));

    this.billCategories.set(mappedData);

    // Always return to the initial 12-card view after rebinding data.
    this.showAllServices.set(false);
  }

  getCategoryIcon(categoryName: string): string {
    const name = categoryName.toLowerCase();

    if (name.includes("landline"))
      return "fa-solid fa-phone";
    if (name.includes("broadband"))
      return "fa-solid fa-wifi";
    if (name.includes("prepaid"))
      return "fa-solid fa-mobile-screen-button";
    if (name.includes("cable"))
      return "fa-solid fa-tv";
    if (name.includes("postpaid"))
      return "fa-solid fa-mobile";
    if (name.includes("datacard"))
      return "fa-solid fa-sim-card";
    if (name.includes("electricity"))
      return "fa-solid fa-bolt";
    if (name.includes("gas"))
      return "fa-solid fa-fire-flame-simple";
    if (name.includes("water"))
      return "fa-solid fa-droplet";
    if (name.includes("insurance"))
      return "fa-solid fa-shield-heart";
    if (name.includes("emi"))
      return "fa-solid fa-file-invoice-dollar";
    if (name.includes("challan"))
      return "fa-solid fa-car";
    if (name.includes("lpg"))
      return "fa-solid fa-gas-pump";
    if (name.includes("fastag"))
      return "fa-solid fa-road";
    if (name.includes("voucher"))
      return "fa-solid fa-gift";
    if (name.includes("municipality"))
      return "fa-solid fa-building-columns";
    if (name.includes("dth"))
      return "fa-solid fa-satellite-dish";
    if (name.includes("credit card"))
      return "fa-solid fa-credit-card";

    return "fa-solid fa-receipt";
  }

  getCategoryGradient(
    categoryName: string,
  ): string {
    const name = categoryName.toLowerCase();

    if (name.includes("electricity"))
      return "linear-gradient(135deg, #ffd166, #ff9f1c)";
    if (name.includes("water"))
      return "linear-gradient(135deg, #48cae4, #0077b6)";
    if (
      name.includes("gas") ||
      name.includes("lpg")
    )
      return "linear-gradient(135deg, #ff9a8b, #ff6a00)";
    if (name.includes("credit card"))
      return "linear-gradient(135deg, #7b2ff7, #f107a3)";
    if (name.includes("insurance"))
      return "linear-gradient(135deg, #00c9a7, #92fe9d)";
    if (name.includes("fastag"))
      return "linear-gradient(135deg, #43cea2, #185a9d)";
    if (name.includes("emi"))
      return "linear-gradient(135deg, #667eea, #764ba2)";
    if (
      name.includes("broadband") ||
      name.includes("wifi")
    )
      return "linear-gradient(135deg, #36d1dc, #5b86e5)";

    return "linear-gradient(135deg, #ff7a18, #af002d)";
  }

  trackById(
    index: number,
    item: BillPaymentCard,
  ): number {
    return item.id;
  }

  /**
   * Existing function name is retained.
   * The first click now opens the biller/operator popup.
   */
  goToProviderList(
    category: BillPaymentCard,
  ): void {
    if (category.status !== "active") {
      return;
    }

    this.selectedCategory = category;
    this.isBillerOperatorPopupOpen = true;
  }

  closeBillerOperatorPopup(): void {
    this.isBillerOperatorPopupOpen = false;
    this.selectedCategory = null;
  }

  /**
   * The existing provider route is retained and runs
   * after customer details, operator lookup, amount,
   * and payment mode are selected.
   */
  onBillerOperatorCompleted(
    details: BillerOperatorSelectionDTO,
  ): void {
    const category = this.selectedCategory;

    if (!category) {
      return;
    }

    this.isBillerOperatorPopupOpen = false;

    this.router.navigate(
      ["/bbps/providers", category.categoryId],
      {
        queryParams: {
          categoryName: category.name,
        },
        state: {
          billerOperatorDetails: details,
        },
      },
    );

    this.selectedCategory = null;
  }
}
